import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";

import { toast } from "react-toastify";
import openSocket from "../../services/socket-io";
import clsx from "clsx";

import { Paper, makeStyles, Box, Button, Collapse, Typography } from "@material-ui/core";

import ContactDrawer from "../ContactDrawer";
import MessageInput from "../MessageInput/";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtons";
import MessagesList from "../MessagesList";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import toastError from "../../errors/toastError";
import { isMetaCallStageControlConfigured } from "../../services/metaCallStageControl";

const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    minHeight: 0,
    position: "relative",
    overflow: "hidden",
  },

  ticketInfo: {
    maxWidth: "50%",
    flexBasis: "50%",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "80%",
      flexBasis: "80%",
    },
  },
  ticketActionButtons: {
    maxWidth: "50%",
    flexBasis: "50%",
    display: "flex",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
      flexBasis: "100%",
      marginBottom: "5px",
    },
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  mainWrapperShift: {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: theme.spacing(1),
  },
}));

const Ticket = () => {
  const { ticketId } = useParams();
  const history = useHistory();
  const classes = useStyles();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [audioPanelOpen, setAudioPanelOpen] = useState(false);

  useEffect(() => {
    setAudioPanelOpen(false);
  }, [ticketId]);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchTicket = async () => {
        try {
          const { data } = await api.get("/tickets/" + ticketId);

          setContact(data.contact);
          setTicket(data);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      void fetchTicket();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [ticketId, history]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("connect", () => socket.emit("joinChatBox", ticketId));

    socket.on("ticket", (data) => {
      if (data?.ticket && data.action === "update" && String(data.ticket.id) === String(ticketId)) {
        setTicket(data.ticket);
      }

      if (data?.ticketId != null && data.action === "delete" && String(data.ticketId) === String(ticketId)) {
        toast.info("La conversación ya no está disponible.");
        history.push("/tickets");
      }
    });

    socket.on("contact", (data) => {
      if (data.action === "update") {
        setContact((prevState) => {
          if (prevState.id === data.contact?.id) {
            return { ...prevState, ...data.contact };
          }
          return prevState;
        });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [ticketId, history]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  return (
    <div className={classes.root} id="drawer-container">
      <Paper
        variant="outlined"
        elevation={0}
        className={clsx(classes.mainWrapper, {
          [classes.mainWrapperShift]: drawerOpen,
        })}
      >
        <TicketHeader loading={loading}>
          <div className={classes.ticketInfo}>
            <TicketInfo
              contact={contact}
              ticket={ticket}
              onClick={handleDrawerOpen}
            />
          </div>
          <div className={classes.ticketActionButtons}>
            <TicketActionButtons ticket={ticket} />
          </div>
        </TicketHeader>
        {/* META_AUDIO_TICKET_STAGING_V1 */}
        {!loading &&
          ticket &&
          String(ticket.id) === String(ticketId) &&
          ticket.status === "open" &&
          !ticket.isGroup &&
          ["localhost", "127.0.0.1"].includes(window.location.hostname) &&
          isMetaCallStageControlConfigured() && (
            <Box
              px={2}
              py={1}
              style={{
                flexShrink: 0,
                borderBottom: "1px solid #dddddd"
              }}
            >
              <Button
                size="small"
                variant="outlined"
                color="primary"
                aria-expanded={audioPanelOpen}
                onClick={() => setAudioPanelOpen(open => !open)}
              >
                {audioPanelOpen
                  ? "Cerrar diagnostico de audio"
                  : "Abrir controles de audio - staging"}
              </Button>

              <Typography
                variant="caption"
                color="textSecondary"
                style={{ display: "block", marginTop: 5 }}
              >
                Solo prueba local de dispositivos. Sin llamada conectada,
                sin transmision de audio y sin contacto con Meta.
              </Typography>

              <Collapse in={audioPanelOpen} unmountOnExit>
                <iframe
                  title="Diagnostico local de audio MandaExpress"
                  src="/meta-audio-test.html"
                  allow="microphone; autoplay"
                  style={{
                    display: "block",
                    width: "100%",
                    height: 470,
                    marginTop: 8,
                    border: "1px solid #dddddd",
                    borderRadius: 8,
                    backgroundColor: "#ffffff"
                  }}
                />
              </Collapse>
            </Box>
          )}
        <ReplyMessageProvider>
          <MessagesList
            ticketId={ticketId}
            isGroup={ticket.isGroup}
          ></MessagesList>
          <MessageInput ticketStatus={ticket.status} />
        </ReplyMessageProvider>
      </Paper>
      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        loading={loading}
      />
    </div>
  );
};

export default Ticket;
